import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Smartphone, CheckCircle2, Loader2, ArrowRight } from "lucide-react";

/**
 * Page de vérification du téléphone via OTP SMS.
 * Étape 1 : confirmer/saisir le numéro → envoi du SMS
 * Étape 2 : saisir le code 6 chiffres reçu
 */
export default function VerifyPhone() {
  const { user, refreshMe } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const destination = params.get("next") === "transporter" || user?.role === "transporter" ? "/transporter" : "/shipper";
  const [step, setStep] = useState(1); // 1 = saisir tel, 2 = saisir code
  const [phone, setPhone] = useState(user?.phone || "");
  const [code, setCode] = useState("");
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [phoneMasked, setPhoneMasked] = useState("");
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (user?.phone_verified) {
      toast.info("Téléphone déjà vérifié");
      navigate(destination);
    }
  }, [user, navigate, destination]);

  // Décompte du cooldown entre 2 envois
  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setInterval(() => setCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(id);
  }, [cooldown]);

  const sendCode = async () => {
    if (!phone.trim()) {
      toast.error("Numéro requis");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/auth/otp/send", { phone: phone.trim() });
      setPhoneMasked(data.phone_masked || phone);
      setStep(2);
      setCooldown(60);
      toast.success("Code envoyé par SMS");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  const verifyCode = async () => {
    if (!/^\d{4,8}$/.test(code)) {
      toast.error("Code invalide (4 à 8 chiffres)");
      return;
    }
    setVerifying(true);
    try {
      await api.post("/auth/otp/verify", { code: code.trim(), phone: phone.trim() });
      toast.success("Téléphone vérifié !");
      await refreshMe?.();
      setTimeout(() => navigate(destination), 600);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-64px)] flex items-center justify-center px-6 py-10" data-testid="verify-phone-page">
      <div className="max-w-md w-full">
        <div className="overline text-accent mb-2">— Vérification</div>
        <h1 className="font-display font-black text-4xl tracking-tighter mb-3">
          {step === 1 ? "Vérifiez votre téléphone" : "Entrez le code reçu"}
        </h1>
        <p className="text-sm text-neutral-600 mb-8">
          {step === 1
            ? "Pour sécuriser votre compte, nous envoyons un code à 6 chiffres par SMS. Ce numéro sera aussi utilisé pour les notifications de missions."
            : `Code envoyé au ${phoneMasked}. Vérifiez vos SMS — le code expire dans 10 minutes.`}
        </p>

        {step === 1 && (
          <div className="space-y-4">
            <div>
              <Label className="overline">Numéro de mobile</Label>
              <div className="relative mt-1">
                <Smartphone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
                <Input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="06 12 34 56 78"
                  className="pl-9 h-12 rounded-md text-base"
                  data-testid="verify-phone-input"
                />
              </div>
              <p className="text-xs text-neutral-500 mt-1">
                Formats acceptés : 06 12 34 56 78, +33 6 12 34 56 78
              </p>
            </div>
            <Button
              onClick={sendCode}
              disabled={sending}
              className="w-full rounded-full bg-[#1F4032] hover:bg-[#2A5443] text-white gap-2 h-12 text-base"
              data-testid="verify-send-btn"
            >
              {sending ? <><Loader2 className="w-4 h-4 animate-spin" /> Envoi…</>
                : <>Recevoir mon code par SMS <ArrowRight className="w-4 h-4" /></>}
            </Button>
            <button
              type="button"
              onClick={() => navigate(destination)}
              className="w-full text-sm text-neutral-500 hover:text-black underline underline-offset-4 mt-2"
              data-testid="verify-skip-btn"
            >
              Passer pour le moment
            </button>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <div>
              <Label className="overline">Code reçu</Label>
              <Input
                type="text"
                inputMode="numeric"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 8))}
                placeholder="······"
                className="h-14 text-center text-2xl font-mono tracking-[0.5em] rounded-md"
                maxLength={8}
                autoFocus
                data-testid="verify-code-input"
              />
            </div>
            <Button
              onClick={verifyCode}
              disabled={verifying || code.length < 4}
              className="w-full rounded-full bg-[#1F4032] hover:bg-[#2A5443] text-white gap-2 h-12 text-base"
              data-testid="verify-confirm-btn"
            >
              {verifying ? <><Loader2 className="w-4 h-4 animate-spin" /> Vérification…</>
                : <><CheckCircle2 className="w-4 h-4" /> Valider mon numéro</>}
            </Button>
            <div className="flex justify-between text-sm pt-2">
              <button
                type="button"
                onClick={() => setStep(1)}
                className="text-neutral-500 hover:text-black underline underline-offset-4"
                data-testid="verify-back-btn"
              >
                ← Modifier le numéro
              </button>
              <button
                type="button"
                onClick={sendCode}
                disabled={cooldown > 0 || sending}
                className="text-[#1F4032] hover:underline disabled:text-neutral-400 disabled:no-underline"
                data-testid="verify-resend-btn"
              >
                {cooldown > 0 ? `Renvoyer dans ${cooldown}s` : "Renvoyer le code"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
